import { useCallback } from "react";
import { mutate } from "swr";
import { mutateCache, useApi } from "../api";
import {
  HrailConfigurationSegment,
  UpsertHrailConfigurationSegmentBody,
} from "./types/configurations";

export const useHrailConfigurationSegmentsApi = () => {
  const api = useApi();

  const invalidate = useCallback((configurationId: string) => {
    mutate(`/configurations/${configurationId}`);
    mutateCache([`/configurations/${configurationId}/hrail/segments`, `/configurations/${configurationId}/items`]);
  }, []);

  const insertSegment = useCallback(
    async (configurationId: string, body: UpsertHrailConfigurationSegmentBody): Promise<{ id: string }> => {
      const res = await api.post<{ id: string }, UpsertHrailConfigurationSegmentBody>(
        `/configurations/${configurationId}/hrail/segments`,
        body,
      );
      invalidate(configurationId);
      return res;
    },
    [api, invalidate],
  );

  const updateSegment = useCallback(
    async (
      configurationId: string,
      segmentId: string,
      body: UpsertHrailConfigurationSegmentBody,
    ): Promise<HrailConfigurationSegment> => {
      const res = await api.putWithResult<UpsertHrailConfigurationSegmentBody, HrailConfigurationSegment>(
        `/configurations/${configurationId}/hrail/segments/${segmentId}`,
        body,
      );
      invalidate(configurationId);
      return res;
    },
    [api, invalidate],
  );

  const deleteSegment = useCallback(
    async (configurationId: string, segmentId: string): Promise<void> => {
      await api.del(`/configurations/${configurationId}/hrail/segments/${segmentId}`);
      invalidate(configurationId);
    },
    [api, invalidate],
  );

  return {
    insertSegment,
    updateSegment,
    deleteSegment,
  };
};
